import type { ReactNode } from 'react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

export type IntegrationStatus =
  | { label: string; tone: 'connected' | 'off' | 'error' | 'loading' | 'ready' };

interface IntegrationTileProps {
  name: string;
  description: string;
  logo: ReactNode;
  status: IntegrationStatus;
  onClick: () => void;
}

export const STATUS_TONE_CLASSES: Record<IntegrationStatus['tone'], string> = {
  connected: 'border-transparent bg-success/10 text-success',
  ready: 'border-transparent bg-info/10 text-info',
  off: 'border-transparent bg-muted text-muted-foreground',
  error: 'border-transparent bg-destructive/10 text-destructive',
  loading: 'border-transparent bg-muted text-muted-foreground',
};

export function IntegrationTile({ name, description, logo, status, onClick }: IntegrationTileProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'group flex aspect-square w-full flex-col items-center justify-center gap-3 rounded-xl border bg-card p-5 text-center shadow-sm transition-all',
        'hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      )}
    >
      <div className="transition-transform group-hover:scale-105">{logo}</div>
      <div className="min-w-0 space-y-1">
        <div className="text-sm font-semibold text-foreground">{name}</div>
        <p className="line-clamp-2 text-xs text-muted-foreground">{description}</p>
      </div>
      <Badge variant="outline" className={cn('text-[11px] font-medium', STATUS_TONE_CLASSES[status.tone])}>
        {status.label}
      </Badge>
    </button>
  );
}
